import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import LoaderTransparent from "../../components/ui/LoaderTransparent";
import { getEmpresaSsoId, usuariosApi } from "../../api/usuariosApi";
import { useGlobalContext } from "../../Config";

const EMPTY = {
  id: null,
  nombre: "",
  apellido: "",
  usuario: "",
  contrasena: "",
  empresa: null,
  perfil: "",
  vendedorCodigo: "",
};

export default function Perfil() {
  const toast = useRef(null);
  const { brand } = useGlobalContext();
  const empresaSso = getEmpresaSsoId(brand);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [initialForm, setInitialForm] = useState(EMPTY);

  useEffect(() => {
    const cargar = async () => {
      try {
        setLoading(true);
        const actual = await usuariosApi.obtenerActual();
        const username =
          actual?.usuario ||
          sessionStorage.getItem("username") ||
          sessionStorage.getItem("user") ||
          "";

        const loadedForm = {
          ...EMPTY,
          id: actual?.id ?? null,
          nombre: actual?.nombre || sessionStorage.getItem("nombre") || "",
          apellido:
            actual?.apellido || sessionStorage.getItem("apellido") || "",
          usuario: username,
          contrasena: "",
          empresa: empresaSso,
          perfil: sessionStorage.getItem("perfil") || "",
          vendedorCodigo: sessionStorage.getItem("vendedorCodigo") || "",
        };

        if (actual?.id) {
          sessionStorage.setItem("usuario_id", String(actual.id));
        }
        setForm(loadedForm);
        setInitialForm(loadedForm);
      } catch (error) {
        showError(error, "No fue posible cargar el perfil desde SSO");
      } finally {
        setLoading(false);
      }
    };

    cargar();
  }, [empresaSso]);

  const setField = (field, value) =>
    setForm((previous) => ({ ...previous, [field]: value }));

  const hasChanges = useMemo(() => {
    const normalize = (value) => String(value ?? "").trim();
    return (
      normalize(form.nombre) !== normalize(initialForm.nombre) ||
      normalize(form.apellido) !== normalize(initialForm.apellido) ||
      normalize(form.usuario) !== normalize(initialForm.usuario) ||
      normalize(form.contrasena) !== ""
    );
  }, [form, initialForm]);

  const guardar = async () => {
    try {
      if (!form.id) throw new Error("No se pudo identificar el usuario SSO");
      if (!String(form.nombre || "").trim()) {
        throw new Error("Debe informar nombre");
      }
      if (!String(form.apellido || "").trim()) {
        throw new Error("Debe informar apellido");
      }
      if (!String(form.usuario || "").trim()) {
        throw new Error("Debe informar usuario/email");
      }

      setSaving(true);
      await usuariosApi.actualizarDatosPropios(form.id, form);

      sessionStorage.setItem("nombre", form.nombre || "");
      sessionStorage.setItem("apellido", form.apellido || "");
      sessionStorage.setItem("username", form.usuario || "");
      sessionStorage.setItem("user", form.usuario || "");

      toast.current?.show({
        severity: "success",
        summary: "Éxito",
        detail: form.contrasena
          ? "Perfil y contraseña actualizados correctamente"
          : "Perfil actualizado correctamente",
        life: 4000,
      });
      const savedForm = { ...form, contrasena: "" };
      setForm(savedForm);
      setInitialForm(savedForm);
    } catch (error) {
      showError(error, "No fue posible actualizar el perfil");
    } finally {
      setSaving(false);
    }
  };

  const showError = (error, fallback) => {
    const data = error?.response?.data;
    const detail =
      (typeof data === "string" && data) ||
      data?.mensaje ||
      data?.message ||
      error?.message ||
      fallback;
    toast.current?.show({
      severity: "error",
      summary: "Error",
      detail,
      life: 5000,
    });
  };

  return (
    <div className="p-2">
      <Toast ref={toast} position="top-right" />
      <Card className="bg-blue-50 border-bottom-1 border-blue-200 py-2 px-3 mb-2">
        <h3 className="m-0 text-blue-700">Mi perfil SSO</h3>
      </Card>

      <Card style={{ position: "relative" }}>
        {loading && <LoaderTransparent coverParent overlayTint="transparent" />}

        <div className="grid p-fluid">
          <div className="field col-12 md:col-6">
            <label className="font-medium block mb-2">Nombre</label>
            <InputText
              value={form.nombre}
              onChange={(e) => setField("nombre", e.target.value)}
            />
          </div>

          <div className="field col-12 md:col-6">
            <label className="font-medium block mb-2">Apellido</label>
            <InputText
              value={form.apellido}
              onChange={(e) => setField("apellido", e.target.value)}
            />
          </div>

          <div className="field col-12 md:col-6">
            <label className="font-medium block mb-2">Usuario / email</label>
            <InputText
              value={form.usuario}
              onChange={(e) => setField("usuario", e.target.value)}
            />
          </div>

          <div className="field col-12 md:col-6">
            <label className="font-medium block mb-2">Nueva contraseña</label>
            <Password
              value={form.contrasena}
              onChange={(e) => setField("contrasena", e.target.value)}
              feedback={false}
              toggleMask
              placeholder="Dejar vacío para no modificar"
            />
          </div>

          <div className="field col-12 md:col-4">
            <label className="font-medium block mb-2">Perfil</label>
            <Tag value={form.perfil || "-"} severity="info" />
          </div>

          <div className="field col-12 md:col-4">
            <label className="font-medium block mb-2">Vendedor</label>
            <div className="font-medium">{form.vendedorCodigo || "-"}</div>
          </div>

          <div className="col-12">
            <div className="flex justify-content-end">
              <Button
                label="Guardar cambios"
                icon="pi pi-save"
                loading={saving}
                disabled={!hasChanges || loading || saving}
                tooltip={
                  !hasChanges
                    ? "Modifique algún dato para guardar"
                    : "Guardar cambios"
                }
                tooltipOptions={{ position: "top" }}
                onClick={guardar}
                className="w-auto"
              />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
